<script setup lang="ts">
import { computed, ref } from 'vue';
import { ModuleFolderMapping } from '@/models/RobotFinder';
import gettext from '@/i18n/gettext';
const { $gettext } = gettext;

interface ModuleItem {
  key: string;
  module: ModuleFolderMapping[string];
}

interface ModuleGroup {
  type: string;
  items: ModuleItem[];
}

const props = withDefaults(
  defineProps<{
    modules: Array<{ key: string; module: ModuleFolderMapping[string] }>;
    selectedKeys?: string[];
    title?: string;
    showSearch?: boolean;
    showDelete?: boolean;
    showAlert?: boolean;
    alertMessage?: string;
    searchLabel?: string;
    isTransparent?: boolean;
    groupByType?: boolean;
  }>(),
  {
    selectedKeys: () => [],
    title: '',
    showSearch: false,
    showDelete: false,
    showAlert: false,
    alertMessage: '',
    searchLabel: '',
    isTransparent: true,
    groupByType: true,
  },
);

const searchTerm = ref('');
const expanded = ref(['Base Drive', 'Drive', 'Link', 'Tool']);
const filteredModules = computed(() => {
  const term = searchTerm.value?.toLowerCase();
  if (!term) {
    // modules is now already an array in the parent
    return props.modules;
  }

  return props.modules.filter(({ module }) =>
    module.moduleName.toLowerCase().includes(term),
  );
});

const displayedModules = computed<ModuleGroup[] | ModuleItem[]>(() => {
  const reversed = [...filteredModules.value].reverse();

  if (!props.groupByType) {
    // Flat list
    return reversed;
  }

  // Grouped version: convert to array for TS
  const groupsMap = new Map<string, ModuleItem[]>(); // ("BaseDrive", ModuleItem[])
  reversed.forEach((item: ModuleItem) => {
    const type =
      item.module.moduleType === 'BaseDrive'
        ? 'Base Drive'
        : item.module.moduleType;
    if (!groupsMap.has(type)) {
      groupsMap.set(type, []);
    }
    groupsMap.get(type)?.push(item);
  });

  // Convert Map to array for easier iteration in template
  return Array.from(groupsMap.entries()).map(([type, items]) => ({
    type,
    items,
  }));
});

const parsedDimensions = (partName: string) => {
  const parts = partName.split('-');

  if (partName.startsWith('BD')) {
    const diameter = parts[0].replace(/^\D+/, ''); // remove letters
    return [
      {
        label: $gettext('Output'),
        value: diameter,
        icon: 'mdi-diameter-variant',
      },
    ];
  } else if (
    parts.length === 1 ||
    Number.isNaN(Number(parts[1])) ||
    Number.isNaN(Number(parts[2]))
  ) {
    // Format: BDxxx (single diameter)
    const diameter = parts[0].replace(/^\D+/, ''); // remove letters
    return [
      {
        label: $gettext('Input'),
        value: diameter,
        icon: 'mdi-diameter-variant',
      },
      {
        label: $gettext('Output'),
        value: diameter,
        icon: 'mdi-diameter-variant',
      },
    ];
  }
  if (parts.length === 3) {
    // Format: BDxxx-xxx-xxx
    const input = parts[0].replace(/^\D+/, '');
    const output = parts[1];
    const length = parts[2];
    return [
      {
        label: $gettext('Input'),
        value: input,
        icon: 'mdi-diameter-variant',
      },
      {
        label: $gettext('Output'),
        value: output,
        icon: 'mdi-diameter-variant',
      },
      {
        label: $gettext('Length'),
        value: length,
        icon: 'mdi-arrow-expand-vertical',
      },
    ];
  }
  return [];
};

const emit = defineEmits(['on-click']);
</script>

<template>
  <v-sheet
    class="left-panel-sheet"
    :color="isTransparent ? 'transparent' : 'surface'"
    rounded="xl"
  >
    <h3 v-if="title" class="ma-4">{{ title }}</h3>
    <div class="d-flex flex-column">
      <v-alert v-if="showAlert" class="ma-3" type="error" outlined>
        <template #prepend>
          <span class="mdi mdi-robot-confused"></span>
        </template>
        {{ alertMessage }}
      </v-alert>
    </div>
    <!-- Search bar -->
    <v-text-field
      v-if="showSearch"
      v-model="searchTerm"
      :label="searchLabel"
      prepend-inner-icon="mdi-magnify"
      clearable
      dense
      class="search-bar flex-shrink-0 d-flex flex-column mx-3"
    />

    <!-- Cards container -->
    <div class="cards-container">
      <!-- Grouped view -->
      <template v-if="groupByType">
        <v-expansion-panels v-model="expanded" multiple>
          <v-expansion-panel
            v-for="group in displayedModules as ModuleGroup[]"
            :key="group.type"
            :value="group.type"
            elevation="0"
          >
            <v-expansion-panel-title
              >{{ group.type }}
              {{ $gettext('Modules') }}</v-expansion-panel-title
            >
            <v-expansion-panel-text class="pt-2">
              <v-row>
                <v-col
                  v-for="(item, index) in group.items"
                  :key="item.key"
                  cols="12"
                  md="12"
                >
                  <v-card
                    class="card-item"
                    :subtitle="
                      item.module.moduleType === 'BaseDrive'
                        ? 'Base Drive'
                        : item.module.moduleType
                    "
                    :title="item.module.moduleName"
                    @click="
                      !showDelete
                        ? emit('on-click', String(item.key))
                        : undefined
                    "
                  >
                    <template v-slot:prepend>
                      <img
                        v-if="
                          item.module.moduleName.match(/^[^\d]*/)?.[0] === 'BD'
                        "
                        src="@/assets/example-module-BD.png"
                        class="thumbnail-img"
                        alt="module image"
                      />
                      <img
                        v-else-if="
                          item.module.moduleName.match(/^[^\d]*/)?.[0] === 'D'
                        "
                        src="@/assets/example-module-D.png"
                        class="thumbnail-img"
                        alt="module image"
                      />
                      <img
                        v-else-if="
                          item.module.moduleName.match(/^[^\d]*/)?.[0] === 'I'
                        "
                        src="@/assets/example-module-I.png"
                        class="thumbnail-img"
                        alt="module image"
                      />
                      <img
                        v-else-if="
                          item.module.moduleName.match(/^[^\d]*/)?.[0] === 'L'
                        "
                        src="@/assets/example-module-L.png"
                        class="thumbnail-img"
                        alt="module image"
                      />
                      <img
                        v-else-if="
                          item.module.moduleName.match(/^[^\d]*/)?.[0] === 'E'
                        "
                        src="@/assets/example-module-E.png"
                        class="thumbnail-img"
                        alt="module image"
                      />
                    </template>
                    <template v-if="showDelete && index === 0" v-slot:append>
                      <v-icon
                        color="critical"
                        icon="mdi-delete"
                        @click="emit('on-click', String(item.key))"
                      ></v-icon>
                    </template>
                    <v-card-text>
                      <v-chip-group column>
                        <v-chip
                          v-for="(dim, index) in parsedDimensions(
                            item.module.moduleName,
                          )"
                          :key="index"
                          :ripple="false"
                          class="ma-1 cursor-default"
                        >
                          <v-icon start :icon="dim.icon" />
                          {{ dim.label }}: {{ dim.value }}mm
                        </v-chip>
                      </v-chip-group>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </template>

      <!-- Flat view -->
      <template v-else>
        <v-row>
          <v-col
            v-for="(item, index) in displayedModules as ModuleItem[]"
            :key="item.key"
            cols="12"
          >
            <v-card
              :class="showDelete ? 'cursor-default' : ''"
              :ripple="false"
              class="card-item"
              :subtitle="
                item.module.moduleType === 'BaseDrive'
                  ? 'Base Drive'
                  : item.module.moduleType
              "
              :title="item.module.moduleName"
              @click="
                !showDelete ? emit('on-click', String(item.key)) : undefined
              "
            >
              <template v-slot:prepend>
                <img
                  v-if="item.module.moduleName.match(/^[^\d]*/)?.[0] === 'BD'"
                  src="@/assets/example-module-BD.png"
                  class="thumbnail-img"
                  alt="module image"
                />
                <img
                  v-else-if="
                    item.module.moduleName.match(/^[^\d]*/)?.[0] === 'D'
                  "
                  src="@/assets/example-module-D.png"
                  class="thumbnail-img"
                  alt="module image"
                />
                <img
                  v-else-if="
                    item.module.moduleName.match(/^[^\d]*/)?.[0] === 'I'
                  "
                  src="@/assets/example-module-I.png"
                  class="thumbnail-img"
                  alt="module image"
                />
                <img
                  v-else-if="
                    item.module.moduleName.match(/^[^\d]*/)?.[0] === 'L'
                  "
                  src="@/assets/example-module-L.png"
                  class="thumbnail-img"
                  alt="module image"
                />
                <img
                  v-else-if="
                    item.module.moduleName.match(/^[^\d]*/)?.[0] === 'E'
                  "
                  src="@/assets/example-module-E.png"
                  class="thumbnail-img"
                  alt="module image"
                />
              </template>
              <template v-if="showDelete && index === 0" v-slot:append>
                <v-icon
                  style="margin-top: -28px"
                  color="critical"
                  icon="mdi-delete"
                  @click="emit('on-click', String(item.key))"
                ></v-icon>
              </template>
              <v-card-text>
                <v-chip-group column>
                  <v-chip
                    v-for="(dim, index) in parsedDimensions(
                      item.module.moduleName,
                    )"
                    :key="index"
                    :ripple="false"
                    class="ma-1 cursor-default"
                  >
                    <v-icon start :icon="dim.icon" />
                    {{ dim.label }}: {{ dim.value }}mm
                  </v-chip>
                </v-chip-group>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </template>
    </div>
  </v-sheet>
</template>

<style lang="scss" scoped>
.card-item {
  width: 100%;
}
.thumbnail-img {
  width: 40px;
  height: 40px;
}
/* Desktop: vertical scroll with fixed height */
@media (min-width: 960px) {
  .left-panel-sheet {
    height: 100%;
    display: flex;
    flex-direction: column;
    border-radius: 16px;
    overflow-y: auto; /* vertical scroll */
    overflow-x: hidden;
  }

  .cards-container {
    display: flex;
    flex-direction: column;
    margin: 0 12px;
  }

  .card-item {
    flex: 0 0 auto;
  }

  .search-bar {
    flex: 0 0 auto;
  }
}

/* Mobile: full height, horizontal scroll */
@media (max-width: 959px) {
  .left-panel-sheet {
    display: flex;
    flex-direction: column;
  }

  .cards-container {
    margin: 0 12px;

    display: flex;
    flex-direction: row;
  }
}

::v-deep(.v-expansion-panel-text__wrapper) {
  padding: 4px;
}

::v-deep(.v-expansion-panel-text) {
  background-color: rgb(var(--v-theme-background));
}
</style>
